import { close } from '@/features/sidebar';
import { servicesApi } from '@/services/api';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { sentryCaptureException } from '@/utils/sentry';
import { buildFlightdeckConversationUrl } from '@/features/support/chat/constants';
import type { CopilotSession } from '@customerio/hydra-copilot';
import { useCopilotTool, useCopilotMessages } from '@customerio/hydra-copilot/react';
import { Button, useModals, useSnackbars } from '@customerio/pluma-components/react';
import { z } from 'zod';

import { PremiumUpgradeModal } from './premium-upgrade-modal';

function PremiumUpgradeButton({
  conversationSummary,
  session,
}: {
  conversationSummary: string;
  session: CopilotSession;
}) {
  const auth = useAuth();
  const modals = useModals();
  const snackbars = useSnackbars();
  const { track } = useAnalytics();
  const messages = useCopilotMessages(session);

  const messageCount = messages.length;
  const hadToolCalls = messages.some(
    (m) => m.type === 'tool_call' && m.tool_calls.some((tc) => tc.name !== 'show_premium_upgrade_form'),
  );

  return (
    <div>
      <Button
        onClick={async () => {
          const modalId = modals.open(PremiumUpgradeModal, {
            conversationSummary,
          });

          const result = await modals.waitForClose(modalId);

          if (result.reason !== 'confirm' || result.data == null) {
            return;
          }

          const { selectedOptions, additionalContext, conversationSummary: summary } = result.data;

          try {
            const messageParts = [
              `Upgrade interests: ${selectedOptions.join(', ')}`,
              ``,
              `Conversation summary: ${summary}`,
              ``,
              `Agent conversation: ${buildFlightdeckConversationUrl(session.id ?? 'unknown')}`,
            ];

            if (additionalContext.length > 0) {
              messageParts.push('', `Additional context from customer: ${additionalContext}`);
            }

            const message = messageParts.join('\n');

            await servicesApi.submitPremiumRequest({
              params: { accountId: auth.account.id },
              body: {
                message,
                features: selectedOptions.join(', '),
                replyTo: auth.user.email,
                consent_to_access: false,
                source: 'AI Agent',
                browser: navigator.userAgent,
                url: window.location.href,
                windowDimensions: `${window.innerWidth}x${window.innerHeight}`,
              },
            });

            track('aiAssistant', 'transferredToAccountManagement', {
              'Account Id': auth.account.id,
              'Is Premium': auth.account.premium,
              'Selected Options': selectedOptions,
              'Message Count': messageCount,
              'Had Tool Calls': hadToolCalls,
            });

            snackbars.create({
              message: 'Thanks! Our Account Management team will reach out shortly.',
              variant: 'success',
            });

            close();
          } catch (error) {
            sentryCaptureException(error);
            snackbars.create({
              message: 'Unable to submit your request. Please try again.',
              variant: 'error',
            });
          }
        }}
      >
        Contact Account Management
      </Button>
    </div>
  );
}

export function ShowPremiumUpgradeFormTool() {
  const auth = useAuth();

  useCopilotTool({
    type: 'client',
    name: 'show_premium_upgrade_form',
    description: `
      Opens a form for the user to contact Customer.io's Account Management team about Premium plan inquiries, upgrades, mid-term adjustments, or plan changes.

      Use this tool when:
      - A user asks about upgrading to Premium
      - A user wants to discuss plan changes, mid-term adjustments, or billing
      - A user needs more profiles, workspaces, or has deliverability needs
      - A user wants more hands-on support or Premium features

      Before calling this tool, give a quick overview of the Premium plan and its benefits. Then call this tool — the form itself will ask the user what they're looking to accomplish.

      Do NOT use show_support_form for upgrade or plan-related requests — use this tool instead.

      <sample-chat>
      User: I want to upgrade to Premium
      Model: Great question! Our Premium plan includes [brief overview]. Let me connect you with our Account Management team.
      Model: show_premium_upgrade_form({ conversationSummary: "Customer asked about upgrading to Premium. They are currently on a self-service plan." })
      </sample-chat>

      <sample-chat>
      User: I need more profiles than my current plan allows
      Model: It sounds like you could benefit from our Premium plan! [brief overview of relevant benefits]. Let me open a form so our Account Management team can help with next steps.
      Model: show_premium_upgrade_form({ conversationSummary: "Customer needs more profiles than their current plan allows. They are hitting profile limits and looking for options to scale." })
      </sample-chat>

      Current account context:
      - Account ID: ${auth.account.id}
      - Account name: ${auth.account.name}
      - Current plan: ${
        auth.account.premium === true
          ? 'Premium'
          : auth.account.premium_trial === true
          ? 'Premium Trial'
          : 'Self-Service'
      }
      - Has CSM: ${auth.account.has_csm === true ? 'Yes' : 'No'}
      - Region: ${auth.account.region}
    `,
    requestSchema: z.toJSONSchema(
      z.object({
        conversationSummary: z
          .string()
          .describe(
            `A summary of the conversation so far for the Account Management team. Include what the customer asked about, any specific needs or concerns mentioned, and relevant account context. The AM team should understand the customer's situation without reading the full chat.`,
          ),
      }),
    ),
    handler() {
      return 'button displayed in chat, the user must click it to open the upgrade form';
    },
    render({ args, session }) {
      return <PremiumUpgradeButton conversationSummary={args.conversationSummary} session={session} />;
    },
  });

  return null;
}
